import MuiDialog, { DialogProps } from "@mui/material/Dialog";
import MuiDialogActions, { DialogActionsProps } from "@mui/material/DialogActions";
import MuiDialogContent from "@mui/material/DialogContent";
import MuiDialogTitle, { DialogTitleProps } from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { CloseIcon } from "common/DS/Icons";
import { Body2, H6 } from "common/DS/Text";
import { useIsBreakpointXS } from "lib/hooks/window";
import React, { ReactElement, ReactNode, useState } from "react";
import classNames from "classnames";
import Paper from "@mui/material/Paper";
import { ButtonProps } from "@mui/material/Button";
import Loader from "common/Loader";
import { TextButton } from "./Buttons";
import { DSButtonProps } from "./types";

export const DIALOG_SIZES = ["xs", "sm", "md", "lg", "xl"];

const BaseDialogPopover = ({ children }: { children: ReactNode }) => (
    <Paper className="ws-dialog-popover">{children}</Paper>
);

export type DSDialogProps = DialogProps & {
    actionsLeft?: ReactNode[];
    actionsRight?: ReactNode[];
    dividers?: boolean;
    handleHide?: undefined | (() => void);
    open: boolean;
    showCloseIcon?: boolean;
    title?: string;
    description?: ReactNode;
    titleClass?: string;
    contentClass?: string;
    maxWidth?: string;
    children?: ReactNode;
    disableScrollLock?: boolean;
    PaperProps?: { className: string };
    className?: string | undefined;
    popoverContent?: ReactNode;
    fullWidth?: boolean;
    fullHeight?: boolean;
    loading?: boolean;
};

/**
 * You can use `BaseDialog` if you want to build a custom dialog with your own
 * Title, Content, and Actions (or lack thereof). You probably won't normally
 * need to do this, but it's here if you want it.
 */
const BaseDSDialog = ({ children, handleHide, open, ...props }: DSDialogProps) => {
    const isXS = useIsBreakpointXS();

    return (
        <MuiDialog open={open} fullScreen={isXS} onClose={handleHide} {...props}>
            {children}
        </MuiDialog>
    );
};

export const BaseDialog = React.memo(BaseDSDialog);

type BaseDialogTitleProps = DialogTitleProps & {
    handleHide?: () => void;
    showCloseIcon?: boolean;
    title: string;
    description?: ReactNode;
};
/**
 * `DialogTitle` is here if you want to use it separately with `BaseDialog`
 */
const BaseDialogTitle = ({
    handleHide = undefined,
    showCloseIcon = false,
    description = null,
    title,
    ...props
}: BaseDialogTitleProps) => (
    <MuiDialogTitle component={H6} {...props}>
        {description && (
            <div className="ws-dialog__title-container gap-xs">
                <span className="ws-dialog__title-container__title">{title}</span>
                <Body2 className="ws-dialog__title-container__description">{description}</Body2>
            </div>
        )}
        {!description && title}
        {showCloseIcon && (
            <IconButton onClick={handleHide}>
                <CloseIcon />
            </IconButton>
        )}
    </MuiDialogTitle>
);

export const DialogTitle = React.memo(BaseDialogTitle);

type BaseDialogActionsProps = DialogActionsProps & {
    className?: string;
};
const BaseDialogActions = ({ className, ...props }: BaseDialogActionsProps) => (
    <MuiDialogActions className={classNames("ws-dialog__actions", className)} {...props} />
);

export const DialogActions = React.memo(BaseDialogActions);

/**
 * This bad boy will do most of the heaviliy lifting for 90% of the dialogs/modals
 * we'll ever want to create. So have at it!
 */
const Dialog = ({
    actionsLeft = [], // Array of buttons that show up on the left-hand side of the footer
    actionsRight = [], // Array of buttons that show up on the right-hand side of the footer
    children, // Body content of the modal
    dividers = true, // Whether the body should show dividers between header and footer
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    handleHide = () => {}, // function that handles when the dialog is hidden
    open, // open/close state for the dialog
    showCloseIcon = true, // whether to show the close icon in the header
    title, // string title for the dialog header
    description, // string description for the dialog header
    titleClass = "", // className to be given to the DialogTitle panel
    contentClass = "", // className to be given to the DialogContent panel
    popoverContent,
    maxWidth = "md",
    fullWidth = false, // Contents are full width of the dialog (so you can make list borders touch the edges)
    fullHeight = false,
    className,
    loading = false,
    ...props // Any other props to pass to the Dialog component
}: DSDialogProps) => {
    const hasLeftActions = actionsLeft.length > 0;
    const hasRightActions = actionsRight.length > 0;
    const hasActions = hasLeftActions || hasRightActions;
    const dialogClassName = classNames(className, {
        "ws-dialog--fullWidth": fullWidth,
        "ws-dialog--fullHeight": fullHeight,
    });

    return (
        <BaseDialog
            open={open}
            handleHide={handleHide}
            {...props}
            maxWidth={maxWidth}
            className={dialogClassName}
        >
            {loading && <Loader />}
            {title && (
                <DialogTitle
                    title={title}
                    description={description}
                    showCloseIcon={showCloseIcon}
                    handleHide={handleHide}
                    className={titleClass}
                />
            )}
            <MuiDialogContent className={contentClass} dividers={dividers}>
                {children}
            </MuiDialogContent>
            {hasActions && (
                <DialogActions>
                    <div>
                        {actionsLeft
                            ?.filter(Boolean)
                            .map((action, idx) =>
                                React.cloneElement(action as ReactElement, { key: idx }),
                            )}
                    </div>
                    <div>
                        {actionsRight
                            ?.filter(Boolean)
                            .map((action, idx) =>
                                React.cloneElement(action as ReactElement, { key: idx }),
                            )}
                    </div>
                </DialogActions>
            )}
            {!!popoverContent && <BaseDialogPopover>{popoverContent}</BaseDialogPopover>}
        </BaseDialog>
    );
};

export default React.memo(Dialog);

export const useConfirmationDialogs = () => {
    const [open, setOpen] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const [action, setAction] = useState(() => () => {});
    const [cancelActionState, setCancelAction] = useState<null | (() => void)>(null);
    const [titleState, setTitle] = useState("");
    const [messageState, setMessage] = useState<ReactNode>("");
    const [buttonTextState, setButtonText] = useState("");
    const [buttonColorState, setButtonColor] =
        useState<NonNullable<DSButtonProps["color"]>>("primary");
    const handleConfirm = () => {
        setOpen(false);
        action();
    };

    const handleCancel = () => {
        if (cancelActionState) {
            cancelActionState();
        }
        setOpen(false);
    };

    const dialog = (
        <Dialog
            dividers={false}
            className="ws-confirmation-dialog"
            open={open}
            title={titleState}
            handleHide={() => setOpen(false)}
            actionsRight={[
                <TextButton key="cancel" onClick={handleCancel} color="primary">
                    Cancel
                </TextButton>,
                <TextButton key="confirm" onClick={handleConfirm} color={buttonColorState}>
                    {buttonTextState}
                </TextButton>,
            ]}
        >
            {messageState}
        </Dialog>
    );

    const showDialog = ({
        title,
        message,
        buttonText,
        onConfirm,
        confirmButtonColor = "primary",
        cancelAction = null,
    }: {
        title: string;
        message: ReactNode;
        buttonText: string;
        onConfirm: () => void;
        confirmButtonColor?: ButtonProps["color"];
        cancelAction?: null | (() => void);
    }) => {
        setAction(() => onConfirm);
        setTitle(title);
        setMessage(message);
        setButtonText(buttonText);
        setButtonColor(confirmButtonColor);
        setOpen(true);
        setCancelAction(() => cancelAction);
    };

    return { dialog, showDialog };
};
